import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Theme, useMediaQuery } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  getReportDownloadLabel,
  reportDateFilterHiddenSx,
  reportDownloadDesktopSx,
} from "screens/Reports/helpers/reportsToolbarStyles";
import ReportsSearchField from "screens/Reports/components/ReportsSearchField";
import ReportsRecordList, {
  ReportRecord,
} from "screens/Reports/components/ReportsRecordList";
import moment from "moment";
import reportsTokens from "screens/Reports/Reports.tokens";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  reportsLoadingRowHeight,
  reportsTableCardSx,
  reportsTableIdentitySx,
  reportsTableNameSx,
  reportsTableSubSx,
  reportsTableSx,
} from "screens/Reports/ReportsTable.styles";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import { handleErrorMessage } from "helpers/methods";
import { DateRange, SortingConfig } from "models/interfaces";
import {
  fetchSentRequest,
  fetchSentRequestCount,
} from "screens/MySignRequests/MySignRequestService";

type RequestRow = {
  rowId: string;
  requestName: JSX.Element;
  /** Names this row's selection checkbox for screen readers (WCAG 4.1.2). */
  selectionLabel: string;
  requestText: string;
  emailText: string;
  recipients: number;
  status: string;
  sentOn: string;
};

const HEADERS = [
  { name: "Request", field: "requestName", align: "left", sorting: false },
  { name: "Recipients", field: "recipients", align: "right", sorting: false },
  { name: "Status", field: "status", align: "right", sorting: false },
  { name: "Sent On", field: "sentOn", align: "right", sorting: false },
];

/** The report itself is not status-scoped — the design's Sent requests tab
 * draws no status filter, so every status is requested. */
const ALL_STATUSES = "All";

const DEFAULT_SORTING_CONFIG: SortingConfig[] = [
  { entity: "signRequest", order: "DESC", field: "createdOn" },
];

const toNumber = (val: any) => {
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const parsed = Number(val.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getAny = (obj: any, keys: string[], fallback: any = "") => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }
  return fallback;
};

const RequestsReport = ({
  setLoader,
}: {
  setLoader?: (value: boolean) => void;
}) => {
  // Below `md` the reference swaps the table for cards — see
  // `ReportsRecordList`.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });
  const [pickerDateRange, setPickerDateRange] = useState<DateRange>(dateRange);
  const [filters, setFilters] = useState({
    page: 1,
    rowsPerPage: 10,
    searchText: "",
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, RequestRow>
  >({});

  const convertTableData = (data: any[]): RequestRow[] => {
    return data.map((item: any, index: number) => {
      const requestTitle = getAny(
        item,
        ["emailSubject", "subject", "title", "templateName", "name"],
        "-",
      );
      const requestedByEmail = getAny(
        item,
        [
          "requestedByEmail",
          "requested_by_email",
          "requestedBy.email",
          "email",
        ],
        item?.requestedBy?.email ?? "-",
      );
      const recipients = Array.isArray(item?.signers)
        ? item.signers.length
        : toNumber(
            getAny(
              item,
              ["recipients", "recipientCount", "recipient_count"],
              0,
            ),
          );
      const createdOnRaw = getAny(item, ["createdOn", "created_on"], null);
      const sentOn = createdOnRaw
        ? moment(createdOnRaw).format("MMM DD, YYYY")
        : "-";

      return {
        rowId: `${String(
          getAny(item, ["id", "requestId", "_id"], requestTitle),
        )}-${index}`,
        requestName: (
          <Box sx={reportsTableIdentitySx}>
            <Box sx={reportsTableNameSx} title={String(requestTitle)}>
              {String(requestTitle)}
            </Box>
            <Box sx={reportsTableSubSx} title={String(requestedByEmail)}>
              {requestedByEmail}
            </Box>
          </Box>
        ),
        selectionLabel: `${String(requestTitle)}, sent by ${String(
          requestedByEmail,
        )}`,
        requestText: String(requestTitle),
        emailText: String(requestedByEmail),
        recipients,
        status: String(getAny(item, ["status"], "-")),
        sentOn,
      };
    });
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setLoader?.(true);
      const [dataResponse, countResponse] = await Promise.all([
        fetchSentRequest(
          filters.page,
          DEFAULT_SORTING_CONFIG,
          ALL_STATUSES,
          filters.searchText,
          "",
          dateRange,
        ),
        fetchSentRequestCount(ALL_STATUSES, filters.searchText, "", dateRange),
      ]);

      const rawRows = Array.isArray(dataResponse)
        ? dataResponse
        : Array.isArray(dataResponse?.data)
          ? dataResponse.data
          : Array.isArray(dataResponse?.content)
            ? dataResponse.content
            : [];

      setRows(convertTableData(rawRows));
      setCount(toNumber(countResponse?.count ?? countResponse ?? 0));
    } catch (error: any) {
      handleErrorMessage(error);
      setRows([]);
      setCount(0);
    } finally {
      setIsLoading(false);
      setLoader?.(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.rowsPerPage, filters.searchText, dateRange]);

  useEffect(() => {
    setSelected([]);
    setSelectedRowsMap({});
  }, [filters.searchText, dateRange]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setFilters((prev) => ({ ...prev, page: 1, searchText }));
  };

  const handlePageChange = (_: any, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, page: 1, rowsPerPage }));
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    const foundRow = rows.find((r) => r.rowId === id);
    if (foundRow) {
      setSelectedRowsMap((prev) => ({ ...prev, [id]: foundRow }));
    }
  };

  const handleSelectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => {
      const next = [...prev];
      visibleRowIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.rowId] = row;
      });
      return next;
    });
  };

  const handleDeselectOne = (id: string) => {
    setSelected((prev) => prev.filter((item) => item !== id));
    setSelectedRowsMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };
  const handleDeselectAll = () => {
    const visibleRowIds = rows.map((row) => row.rowId);
    setSelected((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
    setSelectedRowsMap((prev) => {
      const next = { ...prev };
      visibleRowIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const handleDownloadCSV = () => {
    const selectedData = selected
      .map((id) => selectedRowsMap[id])
      .filter(Boolean);

    const headers = ["Request", "Email", "Recipients", "Status", "Sent On"];

    const csvRows = selectedData.map((row) => [
      `"${row.requestText.replace(/"/g, '""')}"`,
      `"${row.emailText.replace(/"/g, '""')}"`,
      row.recipients,
      `"${row.status}"`,
      `"${row.sentOn}"`,
    ]);

    const csvContent = [headers, ...csvRows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Sent_Requests_${moment().format("YYYY-MM-DD")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const records: ReportRecord[] = rows.map((row) => ({
    rowId: row.rowId,
    name: row.requestText,
    sub: row.emailText === "-" ? undefined : row.emailText,
    selectionLabel: row.selectionLabel,
    pairs: [
      { label: "Recipients", value: String(row.recipients) },
      { label: "Status", value: row.status },
      { label: "Sent On", value: row.sentOn },
    ],
  }));

  return (
    <Stack
      spacing={{ xs: 1.75, sm: 2, md: reportsTokens.toolbar.marginBottom }}
    >
      <Stack
        direction={{ xs: "column", xl: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", xl: "center" }}
      >
        <ReportsSearchField
          placeholder="Search sent requests"
          onSearchChange={handleSearchChange}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: reportsTokens.toolbar.gap }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {/*
            The reference's phone and tablet toolbar is the search field alone —
            the date range stays at its default month there.
          */}
          <Box
            sx={{
              ...reportDateFilterHiddenSx,
              width: { xs: "100%", md: 380 },
              flexShrink: 0,
              alignItems: "stretch",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <CustomDateRangePicker
              ariaLabel="Sent requests date range"
              isClearable={false}
              openDateRangePicker={openDateRangePicker}
              setOpenDateRangePicker={setOpenDateRangePicker}
              dateRange={pickerDateRange}
              setDateRange={setPickerDateRange}
              helperFunction={(confirmedRange: any) => {
                setDateRange(confirmedRange);
                setFilters((prev) => ({ ...prev, page: 1 }));
              }}
              disableFuture={false}
              hideAllTime
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadCSV}
            disabled={selected.length === 0}
            sx={reportDownloadDesktopSx}
          >
            {getReportDownloadLabel(selected.length)}
          </Button>
        </Stack>
      </Stack>

      {isCompact ? (
        <ReportsRecordList
          records={records}
          selected={selected}
          onSelectOne={handleSelectOne}
          onDeselectOne={handleDeselectOne}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isLoading={isLoading}
          onDownload={handleDownloadCSV}
          emptyTitle="No requests sent yet"
          emptySub="Requests you send will be listed here with their recipients and status."
          ariaLabel="Sent requests"
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          count={count}
          onPageChange={(nextPage) => handlePageChange(null, nextPage)}
        />
      ) : (
        <CustomTable
          emptyState={{
            icon: "report",
            title: "No requests sent yet",
            body: "Requests sent in the selected range are listed here with their current status.",
          }}
          loadingRowHeight={reportsLoadingRowHeight}
          cardSx={reportsTableCardSx}
          tableSxOverride={reportsTableSx}
          ariaLabel="Sent requests"
          headers={HEADERS}
          rows={rows}
          count={count}
          page={filters.page}
          rowsPerPage={filters.rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          showJumpToPageNumber={false}
          handleOnChangePageInput={handlePageChange}
          showPagination
          isLoading={isLoading}
          showCheckbox={true}
          hideSelectedCount
          highlightSelectedRows
          selected={selected}
          onSelectOne={handleSelectOne}
          onSelectAll={handleSelectAll}
          onDeselectOne={handleDeselectOne}
          onDeselectAll={handleDeselectAll}
        />
      )}
    </Stack>
  );
};

export default RequestsReport;
